import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { map, startWith, debounceTime, distinctUntilChanged, switchMap, takeUntil, finalize } from 'rxjs/operators';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { ExpedienteService } from '@services/expediente/expediente.service';
import { ExpedienteDto } from '@models/expediente/expedienteDto.model';
import { ExpedienteSummaryDto } from '@models/expediente/expedienteSummary.model';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';

@Component({
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    CommonModule
  ],
  selector: 'app-buscar-expediente',
  templateUrl: './buscar-expediente.component.html',
  styleUrls: ['./buscar-expediente.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuscarExpedienteComponent implements OnInit, OnDestroy {
  @Input() disable: boolean = false;
  @Input() required: boolean = false;
  @Input() idExpedienteInput: number = 0;
  @Input() expedienteEntrada: ExpedienteDto = new ExpedienteDto();
  @Input() placeholder: string = 'Buscar por carátula, número o descripción';

  @Output() expedienteSelected = new EventEmitter<ExpedienteDto | undefined>();
  @Output() createNewExpediente = new EventEmitter<void>();

  @ViewChild('typehead', { read: MatAutocompleteTrigger }) autoTrigger!: MatAutocompleteTrigger;

  public entityControl = new FormControl<string | ExpedienteSummaryDto | null>('');
  public filteredExpedientes!: Observable<ExpedienteSummaryDto[]>;
  public entitySelect: number | null = null;
  public isLoading = false;

  private expedientesSummary$ = new BehaviorSubject<ExpedienteSummaryDto[]>([]);
  private destroy$ = new Subject<void>();
  private readonly minSearchLength = 2;

  constructor(
    private expedienteService: ExpedienteService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
  ) {
    this.setupAutocomplete();
  }

  ngOnInit(): void {
    this.loadExpedientesSummary();
    this.initializeFormControl();

    if (this.required) {
      this.entityControl.addValidators([this.requiredValidator.bind(this)]);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupAutocomplete(): void {
    this.filteredExpedientes = this.entityControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      map(value => this.filterExpedientes(value)),
      takeUntil(this.destroy$)
    );
  }

  private initializeFormControl(): void {
    if (this.idExpedienteInput && this.idExpedienteInput > 0) {
      this.loadExpedienteById(this.idExpedienteInput);
    } else if (this.expedienteEntrada?.caratula) {
      this.entityControl.setValue(this.expedienteEntrada.caratula);
    }
  }

  private filterExpedientes(value: string | ExpedienteSummaryDto | null): ExpedienteSummaryDto[] {
    if (!value || typeof value !== 'string') {
      return [];
    }

    const filterValue = value.toLowerCase().trim();

    if (filterValue.length < this.minSearchLength) {
      return [];
    }

    return this.expedientesSummary$.value.filter(expediente =>
      expediente.descripcion.toLowerCase().includes(filterValue) ||
      (expediente.caratula && expediente.caratula.toLowerCase().includes(filterValue))
    );
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const expediente = event.option.value as ExpedienteSummaryDto;
    this.selectExpediente(expediente);
  }

  private selectExpediente(expediente: ExpedienteSummaryDto): void {
    if (!expediente || !expediente.id) {
      this.clearSelection();
      return;
    }

    this.entitySelect = expediente.id;
    this.loadExpedienteById(expediente.id);
  }

  public clearSelection(): void {
    this.entitySelect = null;
    this.entityControl.setValue('');
    this.expedienteSelected.emit(undefined);
  }

  public createNew(): void {
    this.createNewExpediente.emit();
  }

  public displayFn = (expediente: ExpedienteSummaryDto): string => {
    return expediente ? expediente.descripcion : '';
  };

  public trackByExpediente = (index: number, item: ExpedienteSummaryDto): number => {
    return item.id;
  };

  private loadExpedienteById(id: number): void {
    this.isLoading = true;
    this.spinner.show("spBusqueda");

    this.expedienteService.getById(id)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.spinner.hide("spBusqueda");
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data) => {
          this.expedienteEntrada = data;
          this.expedienteSelected.emit(data);
          this.entityControl.setValue(data.caratula || '');
        },
        error: (error) => {
          this.snackBar.showError('Error al cargar el expediente', 'Error');
          this.clearSelection();
        }
      });
  }

  private loadExpedientesSummary(): void {
    this.isLoading = true;
    this.spinner.show("spBusqueda");

    this.expedienteService.getAllExpedienteSummary()
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.spinner.hide("spBusqueda");
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data) => {
          this.expedientesSummary$.next(data);
        },
        error: (error) => {
          this.snackBar.showError('Error al cargar los expedientes', 'Error');
          this.expedientesSummary$.next([]);
        }
      });
  }

  requiredValidator(control: import('@angular/forms').AbstractControl): {[key: string]: any} | null {
    if (!this.entitySelect && this.required) {
      return { 'required': true };
    }
    return null;
  }

   trackByItems(index: number, item: any): any {
    return item?.id || index;
  }
}
